import type {
  GetCurrentUserWorkspaceMembershipsFn,
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
} from '@/features/iam'
import { action, computed, observable } from 'mobx'
import { type Observable } from 'rxjs'
import { BookkeepingManagerViewModel } from '@/features/admin/bookkeeping/BookkeepingManagerViewModel'
import { BundleManagerViewModel } from '@/features/admin/bundle/BundleManagerViewModel'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import { ScopeChangeRequestsViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestsViewModel'
import { StaffingViewModel } from '@/features/admin/staffing/StaffingViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { type FetchJobsFn } from '@/features/jobs'
import { AccountMergeRequestsViewModel } from '@/features/admin/account-linking/AccountMergeRequestsViewModel'
import { type AccountMergeRequestStore } from '@/features/admin/account-linking/AccountMergeRequestStore'
import { type CanViewPayoutsAdminFn } from '@/features/payouts'
import { type BookkeepingManagerStore } from '@/features/admin/bookkeeping/BookkeepingManagerStore'
import {
  type FetchInventoryItemsByIdsFn,
  type ListInventoryItemsFn,
} from '@/features/inventory'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { type WorkspacePostingStore } from '@/features/admin/staffing/WorkspacePostingStore'
import { FeatureToggles } from '@/features/feature-toggles'
import { isRefreshDesign } from '@/infra/design'
import { type ActivationLifecycle, activeWhen } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export class AdminViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether the admin navigation is visible. The refreshed design has no admin
   * navigation; its global sidebar lists the admin pages.
   */
  @observable accessor navVisible = !isRefreshDesign

  readonly bundleManagerVm

  readonly scopeChangeRequestsVm

  readonly staffingVm

  readonly bookkeepingManagerVm

  readonly accountMergeRequestsVm

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _fetchInventoryItemsByIds: FetchInventoryItemsByIdsFn,
    private readonly _bundleStore: BundleStore,
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _listWorkspaceMemberships: ListWorkspaceMembershipsFn,
    private readonly _workspacePostingStore: WorkspacePostingStore,
    private readonly _canViewPayoutsAdmin: CanViewPayoutsAdminFn,
    private readonly _bookkeepingManagerStore: BookkeepingManagerStore,
    private readonly _accountMergeRequestStore: AccountMergeRequestStore,
    private readonly _createFilterStorage: FilterStorageFactory,
    private readonly _fetchJobs: FetchJobsFn,
    private readonly _currentMember$: Observable<Member>,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _hubspotPortalId: string,
  ) {
    this.bundleManagerVm = new BundleManagerViewModel(
      this._newAlert,
      this._createAlertErrorHandler,
      this._createAlertRetryWrapper,
      this._bundleStore,
      this._searchInventoryItems,
      this._fetchInventoryItemsByIds,
      this._getWorkspaceMemberships,
      this._currentMember$,
    )

    this.scopeChangeRequestsVm = new ScopeChangeRequestsViewModel({
      searchInventoryItems: this._searchInventoryItems,
      scopeChangeRequestStore: this._scopeChangeRequestStore,
      listWorkspaceMemberships: this._listWorkspaceMemberships,
      getWorkspaceMemberships: this._getWorkspaceMemberships,
      createFilterStorage: this._createFilterStorage,
      fetchJobs: this._fetchJobs,
      getCurrentMember: this._getCurrentMember,
      closeNav: this.closeNav,
      newAlert: this._newAlert,
      createAlertErrorHandler: this._createAlertErrorHandler,
      createAlertRetryWrapper: this._createAlertRetryWrapper,
    })

    this.staffingVm = new StaffingViewModel({
      workspacePostingStore: this._workspacePostingStore,
      listWorkspaceMemberships: this._listWorkspaceMemberships,
      getWorkspaceMemberships: this._getWorkspaceMemberships,
      createFilterStorage: this._createFilterStorage,
      newAlert: this._newAlert,
      createAlertErrorHandler: this._createAlertErrorHandler,
      createAlertRetryWrapper: this._createAlertRetryWrapper,
      closeNav: this.closeNav,
    })

    this.bookkeepingManagerVm = new BookkeepingManagerViewModel(
      this._bookkeepingManagerStore,
      this._listWorkspaceMemberships,
      this._getWorkspaceMemberships,
      this._createFilterStorage,
      this.closeNav,
      this._newAlert,
      this._createAlertErrorHandler,
      this._createAlertRetryWrapper,
    )

    this.accountMergeRequestsVm = new AccountMergeRequestsViewModel(
      this._accountMergeRequestStore,
      this._newAlert,
      this._createAlertErrorHandler,
      this._createAlertRetryWrapper,
      this._hubspotPortalId,
    )

    // The admin navigation shows the bookkeeping total on every admin page.
    this._lifecycle.manage(
      activeWhen(
        () => this.canManageBookkeeping,
        this.bookkeepingManagerVm.searcher,
      ),
    )
  }

  @computed
  get scopeChangeRequestsCount() {
    return this._scopeChangeRequestStore.scopeChangeRequests.length
  }

  @computed
  get canManageAccountMergeRequests() {
    return this._getWorkspaceMemberships().some(
      (wm) => wm.member.permissions.adminGlobalManageAccountMergeRequests,
    )
  }

  @computed
  get canManageJobScopeChangeRequest() {
    return (
      this._getCurrentMember().user.platformAdmin ||
      this._getWorkspaceMemberships().some(
        (wm) => wm.member.permissions.adminJobScopeChangeManage,
      )
    )
  }

  @computed
  get canManageServices() {
    return this._getWorkspaceMemberships().some(
      (wm) =>
        wm.member.permissions.adminManageGlobalInventoryItems ||
        wm.member.permissions.manageWorkspaceInventoryItems,
    )
  }

  @computed
  get canManageBookkeeping() {
    return FeatureToggles.bookkeeping() && this.hasBookkeepingPermission
  }

  /**
   * Whether any membership may manage bookkeeping. Route loaders check this
   * rather than {@link canManageBookkeeping}, because they run before the
   * bookkeeping toggle is evaluated for the user.
   */
  @computed
  get hasBookkeepingPermission() {
    return this._getWorkspaceMemberships().some(
      (wm) => wm.member.permissions.manageBookkeeping,
    )
  }

  get canManagePayouts() {
    return this._canViewPayoutsAdmin()
  }

  @computed
  get canSeeAdminView() {
    return (
      this.canManageJobScopeChangeRequest ||
      this.canManageServices ||
      this.hasBookkeepingPermission ||
      this.canSeeStaffingManager ||
      this.canManagePayouts ||
      this.canManageAccountMergeRequests
    )
  }

  @computed
  get canSeeStaffingManager() {
    return this._getWorkspaceMemberships().some(
      (wm) =>
        wm.member.permissions.adminStaffingManage &&
        wm.workspace.config.staffing.enabled,
    )
  }

  @action.bound
  closeNav() {
    this.navVisible = false
  }

  @action.bound
  toggleNavVisibility() {
    if (!this.navVisible) {
      this.scopeChangeRequestsVm.changeRequestDetailsVm.modal.close()
      this.staffingVm.staffingDetailsVm.modal.close()
      this.bookkeepingManagerVm.detailsVm.modal.close()
    }

    this.navVisible = !this.navVisible
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
